import { useMemo } from 'react';
import { Image, compressedImageUrl } from '../design-system';
import type { IframePoolOrderItem } from '../api';
import { BorderComet } from './BorderComet';
import { useImageAspect, containedRectStyle } from './containedRect';
import { cardHeight } from './stageLayout';
import { WINNER_IMG } from './winnerImage';
import { CARD_BOX_ASPECT, CARD_CORNER_RADIUS } from './readyStep.constants';
import { useIsSleek } from '../theme';

type Props = {
  item: IframePoolOrderItem;
  /** Colour of the comet racing the card's contour; none without it. */
  cometColor?: string | null;
};

/**
 * The winner's card in its frame (`CARD_FRAME_STYLE`): top-anchored, clipped
 * to its real rect with rounded corners. Keep in sync with CardSlotTile so
 * the reel→winner landing has no jump.
 */
export function WinnerCard({ item, cometColor = null }: Props) {
  const isSleek = useIsSleek();
  // The exact CDN url the card shows — measured for the card's natural aspect
  // so the rounded clip + comet frame hug its real (object-contain) edges.
  const cardSrc = useMemo(
    () => compressedImageUrl({ url: item.imageUrl, ...WINNER_IMG }),
    [item.imageUrl],
  );
  const cardAspect = useImageAspect(cardSrc);

  return (
    // Card — IN FRONT (z-10), top-anchored; `object-bottom` seats its
    // base on the pedestal, then a tiny fixed `translateY(-3px)` lifts
    // it just clear of the plinth (≈3px gap, per design). Kept in PX
    // (not %) so the gap stays a few pixels at any scene size, and
    // small enough that the card's top never reaches the title band
    // above.
    <div
      className="absolute left-1/2 top-0 z-10"
      style={{
        height: cardHeight(isSleek),
        aspectRatio: '232 / 367',
        transform: 'translateX(-50%) translateY(-3px)',
        // Size container so CARD_CORNER_RADIUS's `cqw` scales the
        // rounded clip with the rendered card (see readyStep.constants).
        containerType: 'size',
      }}
    >
      {/* Card clipped to its real (object-contain) rect with rounded
          corners — trims the source PNG's black corner triangles and
          gives the comet a matching contour. Before the aspect is known,
          fill the box with the original object-contain image so the
          placeholder / fade-in are unchanged; once measured, the wrapper
          shrinks to the card's exact rect (same pixels, now clipped) and
          object-cover fills it. */}
      <div
        className="absolute overflow-hidden"
        style={
          cardAspect != null
            ? {
                ...containedRectStyle(cardAspect, CARD_BOX_ASPECT),
                borderRadius: CARD_CORNER_RADIUS,
              }
            : { inset: 0 }
        }
      >
        <Image
          src={item.imageUrl}
          alt={item.title}
          className="absolute inset-0"
          imgClassName={`absolute inset-0 h-full w-full ${
            cardAspect != null ? 'object-cover' : 'object-contain object-bottom'
          }`}
          {...WINNER_IMG}
        />
      </div>
      {/* Comet racing the card's contour, sized to the same measured rect +
          radius as the clip. Its glow bleeds past the box. */}
      {cometColor && (
        <BorderComet color={cometColor} src={cardSrc} radius={CARD_CORNER_RADIUS} />
      )}
    </div>
  );
}
